import React, { useRef, useState } from 'react';
import { Upload, Loader2, X, ChevronLeft, ChevronRight, Star, Plus, Images } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { uploadsAPI } from '../services/api';

const ACCEPT = 'image/jpeg,image/png,image/webp,image/gif';
const MAX_SIZE = 5 * 1024 * 1024;
export const MAX_IMAGES = 6;

const GalleryEditor = ({ images, onChange }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(0);
  const [urlInput, setUrlInput] = useState('');
  const [error, setError] = useState('');

  const remaining = MAX_IMAGES - images.length;

  const addUrl = () => {
    const url = urlInput.trim();
    if (!url) return;
    if (remaining <= 0) { setError(`${MAX_IMAGES} photos maximum`); return; }
    if (images.includes(url)) { setUrlInput(''); return; }
    setError('');
    onChange([...images, url]);
    setUrlInput('');
  };

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setError('');
    if (files.length > remaining) { setError(`Vous pouvez ajouter encore ${remaining} photo(s) maximum`); return; }
    const invalid = files.find(f => !ACCEPT.split(',').includes(f.type));
    if (invalid) { setError(`« ${invalid.name} » : format non supporté (JPG, PNG, WebP ou GIF)`); return; }
    const tooBig = files.find(f => f.size > MAX_SIZE);
    if (tooBig) { setError(`« ${tooBig.name} » dépasse 5 Mo`); return; }
    setUploading(files.length);
    const added = [];
    try {
      for (const file of files) {
        const result = await uploadsAPI.uploadImage(file);
        added.push(result.url);
        setUploading(n => n - 1);
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Échec de l'envoi d'une image");
    } finally {
      setUploading(0);
      if (inputRef.current) inputRef.current.value = '';
    }
    if (added.length) onChange([...images, ...added]);
  };

  const move = (from, to) => {
    if (to < 0 || to >= images.length) return;
    const next = [...images];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    onChange(next);
  };
  const setMain = (idx) => move(idx, 0);
  const remove = (idx) => onChange(images.filter((_, i) => i !== idx));

  return (
    <div data-testid="gallery-editor">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2"><Images size={18} className="text-orange-500" /> Galerie photos *</h3>
        <span className="text-xs text-gray-500" data-testid="gallery-count">{images.length} / {MAX_IMAGES}</span>
      </div>
      <p className="text-xs text-gray-500 mb-3">La première photo est l'image principale (listes, panier, fiche).</p>

      <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mb-3">
        {images.map((url, idx) => (
          <div key={`${url}-${idx}`} data-testid={`gallery-item-${idx}`} className={`relative group aspect-square rounded-lg border-2 bg-gray-50 overflow-hidden ${idx === 0 ? 'border-orange-500' : 'border-gray-200'}`}>
            <img src={uploadsAPI.resolveUrl(url)} alt="" className="w-full h-full object-contain p-1" />
            {idx === 0 && (
              <span data-testid="gallery-main-badge" className="absolute top-1 left-1 bg-orange-500 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded flex items-center gap-0.5"><Star size={10} /> Principale</span>
            )}
            <button type="button" data-testid={`gallery-remove-${idx}`} onClick={() => remove(idx)} title="Retirer" className="absolute top-1 right-1 bg-white/90 text-red-600 rounded-full p-0.5 shadow opacity-0 group-hover:opacity-100 transition-opacity"><X size={14} /></button>
            <div className="absolute bottom-0 inset-x-0 flex items-center justify-between bg-white/90 px-1 py-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
              <button type="button" data-testid={`gallery-left-${idx}`} onClick={() => move(idx, idx - 1)} disabled={idx === 0} className="p-0.5 text-gray-600 hover:text-orange-600 disabled:opacity-30"><ChevronLeft size={14} /></button>
              {idx !== 0 && (
                <button type="button" data-testid={`gallery-set-main-${idx}`} onClick={() => setMain(idx)} title="Définir comme principale" className="p-0.5 text-gray-600 hover:text-orange-600"><Star size={13} /></button>
              )}
              <button type="button" data-testid={`gallery-right-${idx}`} onClick={() => move(idx, idx + 1)} disabled={idx === images.length - 1} className="p-0.5 text-gray-600 hover:text-orange-600 disabled:opacity-30"><ChevronRight size={14} /></button>
            </div>
          </div>
        ))}
        {remaining > 0 && (
          <button
            type="button"
            data-testid="gallery-add-files"
            onClick={() => !uploading && inputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
            className="aspect-square rounded-lg border-2 border-dashed border-gray-300 hover:border-orange-400 hover:bg-orange-50/40 text-gray-500 flex flex-col items-center justify-center gap-1 text-xs transition-colors"
          >
            {uploading ? <Loader2 size={20} className="animate-spin text-orange-500" /> : <Upload size={20} className="text-orange-500" />}
            {uploading ? `Envoi (${uploading})…` : 'Ajouter'}
          </button>
        )}
      </div>
      <input ref={inputRef} data-testid="gallery-file-input" type="file" accept={ACCEPT} multiple className="hidden" onChange={(e) => handleFiles(e.target.files)} />

      <div className="flex gap-2">
        <Input
          data-testid="gallery-url-input"
          value={urlInput}
          onChange={(e) => setUrlInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addUrl(); } }}
          placeholder="ou coller une URL d'image https://... puis Entrée"
          className="h-9 text-sm"
          disabled={remaining <= 0}
        />
        <Button type="button" variant="outline" size="sm" data-testid="gallery-url-add" onClick={addUrl} disabled={remaining <= 0}><Plus size={14} /></Button>
      </div>
      {error && <p data-testid="gallery-error" className="text-xs text-red-600 mt-2">{error}</p>}
    </div>
  );
};

export default GalleryEditor;
